import { type SxProps, type Theme } from "@mui/material";

import { useFieldId } from "ui/forms/use-field-id";
import { mergeSx } from "ui/merge-sx";
import { type TextAreaProps } from "ui/text-area/text-area.types";
import { TextField } from "ui/text-field/text-field";

export const DEFAULT_ROWS = 4;

/**
 * `TextField` wraps its control in an action row, which is content-height and centres its children —
 * right for a single-line field with a button beside it, and fatal for a textarea told to fill a
 * flex ancestor. These three put the row back in the height chain, so a `flex: 1` on the field root
 * reaches the textarea as it did before the control moved inside the row.
 */
const FILL_HEIGHT_ROW_SX: SxProps<Theme> = { flex: 1, minHeight: 0, alignItems: "stretch" };

export const TextArea = ({
  id,
  name,
  rows = DEFAULT_ROWS,
  resize,
  disableAutosize,
  controlSx,
  actionRowSx,
  "data-testid": dataTestId,
  ...rest
}: TextAreaProps) => {
  const fieldId = useFieldId(id, name);

  return (
    <TextField
      {...rest}
      id={fieldId}
      name={name}
      multiline
      rows={rows}
      inputComponent={disableAutosize ? "textarea" : undefined}
      data-testid={dataTestId ?? `${name ?? fieldId}-textarea`}
      actionRowSx={mergeSx(FILL_HEIGHT_ROW_SX, actionRowSx)}
      // Ahead of the consumer's `controlSx` so an explicit override still wins. MUI pins
      // `resize: none` on every multiline input (`InputBase.js:220`), so this is always an override.
      controlSx={mergeSx(
        resize ? { "& .MuiOutlinedInput-input": { resize } } : undefined,
        controlSx,
      )}
    />
  );
};
